import type { PluginActionMessage, ToolView } from "@scoach/types";
import { useToast } from "@scoach/ui";
import { useEffect, useMemo, useState } from "react";

import { api } from "../../../../lib/http.ts";
import { PluginHost } from "../../../plugins/PluginHost.tsx";
import { pluginsApi } from "../../../plugins/api.ts";
import { buildLiveContext } from "../../../plugins/bridge.ts";
import { openPluginUrl } from "../../../plugins/openPluginUrl.ts";
import { useLiveMeetingStore } from "../store.ts";

/**
 * Renders the user's enabled, approved LIVE-surface plugins in the right panel.
 * Each plugin gets read-only live context (scoped) + a bridge that maps its
 * whitelisted actions onto the live meeting (add note, filter tag, open URL).
 */
export function LivePluginsPanel({ meetingId }: { meetingId: string }) {
  const toast = useToast();
  const [plugins, setPlugins] = useState<ToolView[] | null>(null);

  const transcript = useLiveMeetingStore((s) => s.transcript);
  const hints = useLiveMeetingStore((s) => s.hints);
  const followups = useLiveMeetingStore((s) => s.followups);
  const notes = useLiveMeetingStore((s) => s.notes);
  const addNote = useLiveMeetingStore((s) => s.addNote);
  const startedAt = useLiveMeetingStore((s) => s.startedAt);
  const setActiveTag = useLiveMeetingStore((s) => s.setActiveTagFilter);
  const meetingRole = useLiveMeetingStore((s) => s.meetingRole);

  useEffect(() => {
    pluginsApi
      .list("live")
      .then((r) => setPlugins(r.plugins.filter((p) => p.userEnabled)))
      .catch(() => setPlugins([]));
  }, []);

  function elapsed(): string {
    if (!startedAt) return "00:00";
    const s = Math.floor((Date.now() - startedAt) / 1000);
    return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  }

  function handleAction(plugin: ToolView, msg: PluginActionMessage) {
    switch (msg.action) {
      case "add-note": {
        const text = (msg.payload?.text ?? "").trim();
        if (!text) return;
        const note = { t: elapsed(), text: `[${plugin.name}] ${text}` };
        addNote(note);
        void api(`/meetings/${meetingId}/notes`, {
          method: "PUT",
          body: { notes: [...notes, note] },
        }).catch(() => {});
        toast.push({ tone: "success", message: `${plugin.name}: note added` });
        break;
      }
      case "set-tag":
        setActiveTag(msg.payload?.tag ?? null);
        break;
      case "open-url":
        openPluginUrl(msg.payload?.url);
        break;
      default:
        break;
    }
  }

  const ctxFor = (p: ToolView) =>
    buildLiveContext({
      scope: p.dataScope ?? "metadata",
      meetingId,
      role: meetingRole ?? undefined,
      hints,
      followups,
      transcript,
    });

  const enabled = useMemo(() => plugins ?? [], [plugins]);

  if (plugins === null) return <div className="tasks-empty">Loading plugins…</div>;
  if (enabled.length === 0) {
    return (
      <div className="tasks-empty" style={{ padding: 16, textAlign: "center" }}>
        No plugins enabled. Enable approved plugins in the <strong>Marketplace</strong>, or build
        your own in the <strong>Developer hub</strong>.
      </div>
    );
  }

  return (
    <div
      className="scroll"
      style={{ display: "flex", flexDirection: "column", gap: 12, padding: 8 }}
    >
      {enabled.map((p) => (
        <div key={p.id} style={{ display: "flex", flexDirection: "column", minHeight: 320 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-2)", margin: "0 0 6px" }}>
            {p.icon ? `${p.icon} ` : ""}
            {p.name}
            <span style={{ fontWeight: 500, color: "var(--text-3)", marginLeft: 6 }}>
              · {p.dataScope === "full" ? "full context" : "metadata"}
            </span>
          </div>
          <PluginHost
            plugin={p}
            context={ctxFor(p)}
            onAction={(m) => handleAction(p, m)}
            height={360}
          />
        </div>
      ))}
    </div>
  );
}
